import { cn } from '@brew/ui/cn'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * The hero word's fill: the prompt panel's golden-hour shader, rendered to a
 * looping animated WebP (`public/landing/home/hero/golden-hour-word.webp`,
 * an 8s ping-pong of the shader's orange band) and clipped to the letters.
 * Solid brand orange until it loads. Phones and reduced motion get the
 * still frame (1 KB) instead: the 80 KB animation would otherwise compete
 * with the page's first paint on a slow connection. A media-scoped CSS
 * background downloads only where it applies.
 * The headline's tight leading makes the word's box shorter than its glyphs
 * when it is a flex or grid item, and a text-clipped fill is cut at the box,
 * so the bottom padding extends the fill under the descenders ("g", "y")
 * and the matching negative margin keeps the layout where it was.
 */
const GOLDEN_FILL_SRC = '/landing/home/hero/golden-hour-word.webp'
const GOLDEN_FILL_STILL_SRC = '/landing/home/hero/golden-hour-word-still.webp'

export const GOLDEN_WORD_FILL =
  '-mb-[0.2em] bg-[#ff772d] bg-[url(/landing/home/hero/golden-hour-word-still.webp)] bg-cover bg-center bg-clip-text pb-[0.2em] text-transparent [-webkit-background-clip:text] md:motion-safe:bg-[url(/landing/home/hero/golden-hour-word.webp)]'

/**
 * A headline word in the golden-hour fill (the home hero's rotating word,
 * held still). It wraps like the rest of the headline, so a long name
 * ("Constant Contact") breaks between words on a phone instead of clipping.
 * `data-golden-word` lets a headline lay itself out around it (`PageHero`).
 */
export function GoldenWord({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span data-golden-word="" className={cn(GOLDEN_WORD_FILL, className)}>
      {children}
    </span>
  )
}

/**
 * The shader's tones, sampled across its frames: the deep orange it rests
 * on, its mid band, and the golden highlight that rises through it. A fill
 * built from them costs no request and no image decode.
 */
const GOLDEN_STOPS = [
  { offset: '0%', color: '#e15400' },
  { offset: '55%', color: '#ec6620' },
  { offset: '100%', color: '#fbb466' },
] as const

/**
 * A line icon in the golden-hour fill, with no tile behind it: the icon's
 * strokes mask the fill. `shader` (the page hero) is the headline word's
 * own animated WebP over solid brand orange, the same cached image, still
 * under reduced motion; `gradient` (the navbar's menus, on every page) is a
 * static gradient of the shader's tones: no request, no decoding. `id` names
 * the mask and gradient, so it must be unique on the page.
 */
export function GoldenIcon({
  icon: Icon,
  id,
  fill = 'shader',
  className,
}: {
  icon: LucideIcon
  id: string
  fill?: 'shader' | 'gradient'
  className?: string
}) {
  const maskId = `${id}-mask`
  const gradientId = `${id}-fill`
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn('shrink-0', className)}
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="24"
          height="24"
        >
          <Icon width={24} height={24} color="#fff" strokeWidth={1.6} />
        </mask>
        {fill === 'gradient' ? (
          <linearGradient id={gradientId} x1="0" y1="0" x2="0.35" y2="1">
            {GOLDEN_STOPS.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.color}
              />
            ))}
          </linearGradient>
        ) : null}
      </defs>
      <g mask={`url(#${maskId})`}>
        {fill === 'gradient' ? (
          <rect width="24" height="24" fill={`url(#${gradientId})`} />
        ) : (
          <>
            <rect width="24" height="24" fill="#ff772d" />
            {/* A hidden SVG image is not fetched: phones and reduced
                motion load only the still frame, as the word does. */}
            <image
              href={GOLDEN_FILL_SRC}
              width="24"
              height="24"
              preserveAspectRatio="xMidYMid slice"
              className="hidden md:motion-safe:inline"
            />
            <image
              href={GOLDEN_FILL_STILL_SRC}
              width="24"
              height="24"
              preserveAspectRatio="xMidYMid slice"
              className="md:motion-safe:hidden"
            />
          </>
        )}
      </g>
    </svg>
  )
}
